import { BigDecimal } from "@solomon-labs/bigdecimal";

import USDC_ICON from "../../assets/icons/tokens/USDC.svg";
import USDV_ICON from "../../assets/logos/usdv-logomark.png";
import type { TransactionPhase } from "../../hooks/transactions/useTransactionPhase";
import { formatNumber } from "../../utils/format";

/** Bundled USDC / USDv token marks. Imported assets (inlined/emitted by
 *  the build) so the exported widget bundle is self-contained — earlier
 *  these were hardcoded `/img/*` URLs that 404'd outside apps/main. */
export { USDC_ICON, USDV_ICON };

/**
 * Mode- and phase-driven label/icon policy for the USDv Buy/Sell swap surface.
 *
 * Extracted from `SwapContent` so both the connected `SwapContent` panel and
 * the disconnected landing `BuyWidget` share a single source of truth for the
 * Buy↔Sell copy. Kept dependency-light (no `@solomon-labs/constants`, no heavy
 * components) so importing it into `BuyWidget` doesn't pull `SwapContent`'s
 * module graph into the lightweight landing/external-consumer bundle.
 *
 * `SwapContent` re-exports the public helpers + their input types so existing
 * import paths (`@solomon-apps/shared`, `./widgets/swap/SwapContent`) and the
 * `USDvSwapContent` test suite keep working unchanged.
 */

/** Resolve the canonical asset icon for a swap leg, falling back to the
 *  caller-provided icon for non-USDC/USDv tokens (e.g. SOL). */
export function getSwapTokenIcon(symbol: string, fallbackIcon: string): string {
    if (symbol === "USDC") return USDC_ICON;
    if (symbol === "USDv") return USDV_ICON;
    return fallbackIcon;
}

const formatAmount = (value: string): string => {
    // Snapshots contain display amounts with grouping commas. Parsing them as
    // floats stops at the first comma and can turn 10,000 into 10.
    try {
        const amount = new BigDecimal(value.replace(/,/g, ""));
        if (amount.isNaN()) return value;
        return formatNumber(amount, { maximumFractionDigits: 4 });
    } catch {
        return value;
    }
};

/**
 * Inputs to {@link getSwapCtaLabel}.
 */
export interface SwapCtaLabelInputs {
    /** Active transaction phase (`useTransactionPhase`). */
    phase: TransactionPhase;
    /** Buy vs Sell sub-tab. */
    mode: "buy" | "sell";
    /**
     * The USDv leg of the swap (receive for buy, send for sell), already
     * resolved from `snapshot` (frozen at submit) or the live display
     * value before submit. Empty string when no input yet.
     */
    usdvAmount: string;
    /** Whether the user has entered a positive `sendAmount`. */
    hasInput: boolean;
    /**
     * Validation/feedback override from `useSwap` (e.g. "Insufficient
     * USDC"). Overrides the idle/filled label only — error and inflight
     * phases ignore it because the phase-driven copy is more accurate.
     */
    ctaFeedback: string | null;
}

/**
 * Phase- and mode-driven label for the inline submit Button branch of
 * the Swap form.
 *
 * The label encodes a progressive verb chain so the user can read
 * progress from the button text alone:
 *
 *   empty / focused
 *          → "Buy USDv" / "Sell USDv"
 *   filled → "Buy 10 USDv" / "Sell 10 USDv"   (parameterized by the
 *                                              USDv-leg amount)
 *   pending / processing
 *          → "Buying 10 USDv" / "Selling 10 USDv"
 *   error  → "Buy Failed" / "Sell Failed"
 *
 * The `success` / `postSuccess` phases don't use this function — they
 * route through `TransitionButton` and `getSwapSuccessLabel` instead.
 *
 * Exported for tests so the policy is verified directly rather than via
 * a manual tree walk through the component's JSX.
 */
export function getSwapCtaLabel({ phase, mode, usdvAmount, hasInput, ctaFeedback }: SwapCtaLabelInputs): string {
    const isBuy = mode === "buy";
    if (phase === "error") {
        return isBuy ? "Buy Failed" : "Sell Failed";
    }
    if (phase === "pending" || phase === "processing") {
        return isBuy ? `Buying ${usdvAmount} USDv` : `Selling ${usdvAmount} USDv`;
    }
    if (ctaFeedback) return ctaFeedback;
    if (hasInput && usdvAmount) {
        return isBuy ? `Buy ${usdvAmount} USDv` : `Sell ${usdvAmount} USDv`;
    }
    return isBuy ? "Buy USDv" : "Sell USDv";
}

/**
 * Inputs to {@link getSwapSuccessLabel}.
 */
export interface SwapSuccessLabelInputs {
    mode: "buy" | "sell";
    snapshot: {
        sendAmount: string;
        sendSymbol: string;
        receiveAmount: string;
        receiveSymbol: string;
    } | null;
}

/**
 * Phase=success label shown by `TransitionButton`.
 *
 *   Buy success  → "Bought {receiveAmount} {receiveSymbol}"
 *   Sell success → "Sold {sendAmount} {sendSymbol}"
 *
 * Both read off the snapshot so the value stays frozen across the
 * post-success animation window. Falls back to a bare verb if the
 * snapshot is missing (defensive — shouldn't happen in production).
 */
export function getSwapSuccessLabel({ mode, snapshot }: SwapSuccessLabelInputs): string {
    if (!snapshot) return mode === "buy" ? "Bought" : "Sold";
    if (mode === "buy") {
        return `Bought ${formatAmount(snapshot.receiveAmount)} ${snapshot.receiveSymbol}`.trim();
    }
    return `Sold ${formatAmount(snapshot.sendAmount)} ${snapshot.sendSymbol}`.trim();
}

export interface SwapUsdVLabelAmountInputs {
    mode: "buy" | "sell";
    snapshot: {
        sendAmount: string;
        receiveAmount: string;
    } | null;
    sendAmount: string;
    receiveAmountDisplay: string | null | undefined;
}

export function getSwapUsdVLabelAmount({
    mode,
    snapshot,
    sendAmount,
    receiveAmountDisplay
}: SwapUsdVLabelAmountInputs): string {
    return mode === "buy"
        ? (snapshot?.receiveAmount ?? receiveAmountDisplay ?? "")
        : (snapshot?.sendAmount ?? sendAmount);
}
